import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { auth } from '../services/firebase';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signInAnonymously } from 'firebase/auth';
import { UserService } from '../services/UserService';
import { useTenantStore } from './tenantStore';

export const useAuthStore = defineStore('auth', () => {
    const currentUser = ref(null);
    const isLoading = ref(true);
    const error = ref(null);
    const tenantStore = useTenantStore();

    const isGuestUser = computed(() => {
        if (!currentUser.value) return false;
        return currentUser.value.isAnonymous === true || currentUser.value.displayName === 'Anonymous';
    });

    const initializeAuth = () => {
        onAuthStateChanged(auth, async (user) => {
            isLoading.value = true;
            if (user) {
                try {
                    const profile = await UserService.ensureUser(user, tenantStore.tenantName);
                    currentUser.value = profile;
                } catch (err) {
                    console.error('[AuthStore] Failed to sync profile:', err);
                    error.value = err.message;
                }
            } else {
                currentUser.value = null;
            }
            isLoading.value = false;
        });
    };

    const login = async () => {
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
        } catch (err) {
            console.error('[AuthStore] Login failed:', err);
            throw err;
        }
    };

    const loginAsGuest = async () => {
        try {
            await signInAnonymously(auth);
        } catch (err) {
            console.error('[AuthStore] Guest login failed:', err);
            throw err;
        }
    };

    const logout = async () => {
        try {
            await auth.signOut();
            currentUser.value = null;
        } catch (err) {
            console.error('[AuthStore] Logout failed:', err);
            throw err;
        }
    };

    return {
        currentUser,
        isLoading,
        error,
        isGuestUser,
        initializeAuth,
        login,
        loginAsGuest,
        logout
    };
});
